/**
 * The marks of the formatting bar over a door's text (24.10.2026): the subset of Markdown the
 * one parser on the server accepts (`services/richtext.py`). Each step takes the text and the
 * selection and returns both changed; the bar writes the result back into the editor.
 */
const STAR = "*";
/** The selection without the spaces at its edges: the parser refuses `** fett**`. */
function trimmed({ text, start, end }) {
    while (start < end && /\s/.test(text[start] ?? ""))
        start += 1;
    while (end > start && /\s/.test(text[end - 1] ?? ""))
        end -= 1;
    return { text, start, end };
}
/** Stars right before `at` (backwards) or from `at` on (forwards). */
function stars(text, at, backwards) {
    let count = 0;
    while (text[backwards ? at - 1 - count : at + count] === STAR)
        count += 1;
    return count;
}
/** Bold (`**`) or italic (`*`) on and off; a selection over several lines marks each line. */
function emphasis(selected, size) {
    const { text } = selected;
    let { start, end } = trimmed(selected);
    if (start === end) {
        return selected;
    }
    const marker = STAR.repeat(size);
    if (text.slice(start, end).includes("\n")) {
        const lines = text
            .slice(start, end)
            .split("\n")
            .map((line) => {
            const core = line.trim();
            return core === "" ? line : line.replace(core, `${marker}${core}${marker}`);
        })
            .join("\n");
        return { text: text.slice(0, start) + lines + text.slice(end), start, end: start + lines.length };
    }
    // Selected with its stars: the stars count as outside.
    while (end - start > 2 && text[start] === STAR && text[end - 1] === STAR) {
        start += 1;
        end -= 1;
    }
    const around = Math.min(stars(text, start, true), stars(text, end, false));
    // One star is italic, two bold, three both.
    const on = size === 2 ? around >= 2 : around === 1 || around === 3;
    if (on) {
        const next = text.slice(0, start - size) + text.slice(start, end) + text.slice(end + size);
        return { text: next, start: start - size, end: end - size };
    }
    const next = text.slice(0, start) + marker + text.slice(start, end) + marker + text.slice(end);
    return { text: next, start: start + size, end: end + size };
}
/** »- « before every line the selection touches, or off when every filled one has it. */
function list({ text, start, end }) {
    const from = text.lastIndexOf("\n", start - 1) + 1;
    const stop = text.indexOf("\n", end > start && text[end - 1] === "\n" ? end - 1 : end);
    const to = stop === -1 ? text.length : stop;
    const lines = text.slice(from, to).split("\n");
    const filled = lines.filter((line) => line.trim() !== "");
    const listed = filled.length > 0 && filled.every((line) => line.startsWith("- "));
    const block = lines
        .map((line) => (line.trim() === "" ? line : listed ? line.slice(2) : line.startsWith("- ") ? line : `- ${line}`))
        .join("\n");
    return { text: text.slice(0, from) + block + text.slice(to), start: from, end: from + block.length };
}
/** `[Text](https://)` with the address selected, so the next keystrokes type it. */
function link(selected) {
    const { text, start, end } = trimmed(selected);
    const label = text.slice(start, end).replace(/\s*\n\s*/g, " ");
    if (label === "") {
        return selected;
    }
    const head = `${text.slice(0, start)}[${label}](`;
    // eslint-disable-next-line no-restricted-syntax -- the scheme the parser requires, no address
    const address = "https://";
    return { text: `${head}${address})${text.slice(end)}`, start: head.length, end: head.length + address.length };
}
export function applyMark(mark, selected) {
    switch (mark) {
        case "bold":
            return emphasis(selected, 2);
        case "italic":
            return emphasis(selected, 1);
        case "list":
            return list(selected);
        case "link":
            return link(selected);
    }
}
//# sourceMappingURL=format.js.map